import { Box, Typography, Container, Stack } from '@mui/material'
import { Link2Off } from 'lucide-react'

import { IconBadge } from 'components/UI/IconBadge'

/**
 * VRG-268 — what a retired tag resolves to.
 *
 * Reached only by redirect from `GET /verify/:uid` when the scanned tag has been retired,
 * today meaning it was replaced. Before this existed, that scan returned `404 Tag verification
 * failed` as raw JSON, which to the person holding the item is indistinguishable from the
 * product being broken. That is the worst possible moment for an authenticity product to look
 * broken, so the failure gets a real page.
 *
 * WHY IT SAYS SO LITTLE (product decision, 2026-08-11). Whoever is standing here holds either
 * a genuine old tag that came off its item, or a reproduction of one. The server can tell which
 * is physically PLAUSIBLE — `tags.form` says whether the tag was moulded in, sewn, adhered or
 * hung, and only the last comes off without marking the goods — but plausible is not proof, and
 * the page is read by a stranger holding someone's handbag. So it does not narrate what
 * happened: "this tag was replaced" would reassure the first person and instruct the second.
 * It states only that the tag is not valid for this item, and points at the brand.
 *
 * Deliberately absent, and NOT an oversight to be filled in later:
 *   - any mention that a replacement exists, or of the replacement tag
 *   - any accusation, or any wording implying the item is counterfeit
 *   - Vera's identity or a Vera support path. The scanner believes they are dealing with the
 *     brand, and the brand is who can actually look up the item.
 *
 * The page carries no Vera branding of its own because it is served from the company's
 * Vera-hosted installation, on the brand's subdomain, and `App.tsx` themes the whole app from
 * that subdomain. It therefore inherits the brand's identity by construction.
 */
export default function TagReplaced() {
  return (
    <Container maxWidth='sm'>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '80vh',
          textAlign: 'center',
        }}
      >
        <Stack spacing={3} alignItems='center'>
          {/*
            The shared `IconBadge` — the grey circle every state screen in this app opens
            with. Two choices here are made by this page's doctrine above, not by taste:

            GLYPH. A broken link, because the only thing this page asserts is that the tag
            and the item are no longer bound to each other. Every glyph that narrates WHY
            was rejected for the reason the header gives: a rotate/replace mark tells the
            second reader that a live replacement exists, and any warning triangle, shield
            or slash grades the ITEM, which is exactly the accusation this page must not
            make. `Link2Off` is also the glyph `ScanStatusChip` already uses for a severed
            tag binding, so the two surfaces agree.

            TONE. The brand tone, not the alert red. Nothing is broken here: a retired tag
            is a settled, ordinary state, and a red badge above "no longer valid" reads as
            a verdict on a handbag somebody is holding in a shop.
          */}
          <IconBadge icon={Link2Off} />

          <Stack spacing={2} alignItems='center'>
            <Typography variant='h4' component='h1'>
              This tag is no longer valid for this item
            </Typography>

            {/*
              Two sentences, not one joined by an em-dash (§15). lineHeight 1.5 so the copy
              does not read dense on a phone, which is the only device this page is ever
              seen on.
            */}
            <Typography variant='body1' color='text.secondary' sx={{ lineHeight: 1.5 }}>
              If you have questions about this item, please contact the brand you bought it
              from. They can look it up for you.
            </Typography>
          </Stack>
        </Stack>
      </Box>
    </Container>
  )
}
